import { useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { RecordLink } from '../components/RecordLink.js';
import { Pager } from '../components/Pager.js';
import { ListToolbar, ListWindowNotice, type FilterChip } from '../components/ListToolbar.js';
import { downloadExport, useListView } from '../lib/listView.js';

/**
 * Journal — what people have done.
 *
 * The point of an audit trail is not that entries exist; it is that somebody can answer a
 * question with them. Three questions get asked in practice, and the filters are exactly
 * those: *what did this person do*, *what happened to invoices*, *what happened on Tuesday*.
 *
 * Two things this deliberately shows:
 *
 * **The changed fields, not just the fact of a change.** "Modifié" tells you nothing you
 * could act on. "Échéance : 14 août → 21 août" is the reason anybody opens a log.
 *
 * **Where the change came from.** An edit made from a van at 19:40 reads differently from the
 * same edit made at the office, and `origin` is the only thing that can say which.
 *
 * Entries with no detail are still shown rather than hidden. They come from the safety net
 * that records every mutation whether or not its handler described itself, and a trail that
 * quietly omits what it could not explain is worse than one that says "changed".
 *
 * The ordering is a control rather than a set of clickable headings, because these entries are
 * not rows: each carries a variable list of changed fields, and a table wide enough to hold that
 * would be unreadable at the density this screen needs.
 */

type Entry = {
  id: string;
  at: string;
  actor: { id: string; name: string; color: string | null } | null;
  entity: string;
  entityId: string | null;
  action: string;
  detail: Record<string, unknown>;
  reversible: boolean;
  changes: Record<string, { from: unknown; to: unknown }>;
  origin: string;
  context: Record<string, unknown>;
};

type Response = {
  items: Entry[];
  total: number;
  outsideWindow: number;
  page: number;
  pageSize: number;
  pageCount: number;
  sort: string;
  dir: 'asc' | 'desc';
  facets: {
    actors: Array<{ id: string; name: string; count: number }>;
    entities: Array<{ entity: string; count: number }>;
  };
};

/** The record kinds that can be opened. Anything else is named but not linked. */
const OPENABLE: Record<string, 'customer' | 'job' | 'quote' | 'invoice'> = {
  customer: 'customer',
  job: 'job',
  quote: 'quote',
  invoice: 'invoice',
};

const SORTS = ['at', 'entity', 'action', 'actor'] as const;

export function JournalScreen() {
  const { t: translate, i18n } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;
  const locale: Locale = i18n.language === 'en' ? 'en' : 'fr';
  const search = useSearch({ from: '/journal' });
  const navigate = useNavigate();

  const [exportNote, setExportNote] = useState<{ text: string; failed: boolean } | null>(null);
  const [exporting, setExporting] = useState(false);

  /*
   * Words come from the catalogue, keyed by the machine value in the row. That is the whole
   * reason the log stores `voided` and `dueOn` rather than a French sentence: the same entry
   * reads correctly in either language, and switching language re-renders it rather than
   * needing it written twice.
   *
   * An unknown key falls back to the raw value — a new action shows as `refunded` rather than
   * a blank, which is ugly and honest instead of tidy and wrong.
   */
  const label = (group: 'entity' | 'action' | 'field' | 'origin', key: string): string => {
    const translated = t(`audit.${group}.${key}`);
    return translated === `audit.${group}.${key}` ? key : translated;
  };

  const setSearch = (patch: Record<string, unknown>) =>
    navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }), replace: true });

  const view = useListView({
    routeId: '/journal',
    defaultSort: 'at',
    timeBased: true,
    defaultDateField: 'created',
    extra: useMemo(
      () => ({ userId: search.actor, entity: search.entity, on: search.on }),
      [search.actor, search.entity, search.on],
    ),
  });

  const query = useQuery({
    queryKey: ['audit', view.params.toString()],
    queryFn: () => request<Response>(`/audit?${view.params}`),
    placeholderData: (prev) => prev,
  });

  const data = query.data;
  const rows = data?.items ?? [];

  const chips: FilterChip[] = [
    ...(search.actor
      ? [
          {
            id: 'actor',
            label: `${t('audit.actorFilter')} : ${data?.facets.actors.find((a) => a.id === search.actor)?.name ?? search.actor}`,
            onRemove: () => setSearch({ actor: undefined, page: undefined }),
          },
        ]
      : []),
    ...(search.entity
      ? [
          {
            id: 'entity',
            label: `${t('audit.entityFilter')} : ${label('entity', search.entity)}`,
            onRemove: () => setSearch({ entity: undefined, page: undefined }),
          },
        ]
      : []),
    ...(search.on
      ? [{ id: 'on', label: `${t('audit.dayFilter')} : ${search.on}`, onRemove: () => setSearch({ on: undefined, page: undefined }) }]
      : []),
  ];

  const runExport = async () => {
    setExporting(true);
    setExportNote(null);
    try {
      const outcome = await downloadExport('/audit/export', view.exportParams, locale);
      setExportNote({
        text: outcome.truncated
          ? t('list.exportTruncated', { limit: outcome.rows, total: outcome.matched })
          : t('list.exportDone', { count: outcome.rows }),
        failed: outcome.truncated,
      });
    } catch {
      setExportNote({ text: t('list.exportFailed'), failed: true });
    } finally {
      setExporting(false);
    }
  };

  const direction = data?.dir ?? view.dir ?? 'desc';

  return (
    <div className="screen journal">
      <header className="screen__head">
        <div>
          <h1 className="screen__title">{t('nav.journal')}</h1>
          <p className="journal__lede">{t('audit.lede')}</p>
        </div>
      </header>

      <ListToolbar
        view={view}
        chips={chips}
        onClearAll={() => setSearch({ actor: undefined, entity: undefined, on: undefined, page: undefined })}
        total={data?.total}
        onExport={() => void runExport()}
        exporting={exporting}
        exportNote={exportNote?.text ?? null}
        exportFailed={exportNote?.failed}
      >
        <label className="listbar__field">
          {t('audit.actorFilter')}
          <select value={search.actor ?? ''} onChange={(e) => setSearch({ actor: e.target.value || undefined, page: undefined })}>
            <option value="">{t('common.all')}</option>
            {(data?.facets.actors ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.count})
              </option>
            ))}
          </select>
        </label>

        <label className="listbar__field">
          {t('audit.entityFilter')}
          <select value={search.entity ?? ''} onChange={(e) => setSearch({ entity: e.target.value || undefined, page: undefined })}>
            <option value="">{t('common.all')}</option>
            {(data?.facets.entities ?? []).map((e) => (
              <option key={e.entity} value={e.entity}>
                {label('entity', e.entity)} ({e.count})
              </option>
            ))}
          </select>
        </label>

        {/* An ordering control rather than clickable headings: see the note at the top. */}
        <label className="listbar__field">
          {t('job.sort.label')}
          <select value={data?.sort ?? view.sort} onChange={(e) => view.setSort(e.target.value, undefined)}>
            {SORTS.map((key) => (
              <option key={key} value={key}>
                {t(`audit.sort.${key}`)}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="listbar__toggle"
          onClick={() => view.setSort(data?.sort ?? view.sort, direction === 'asc' ? 'desc' : 'asc')}
          aria-label={t(direction === 'asc' ? 'list.sortedAscending' : 'list.sortedDescending')}
        >
          <span aria-hidden="true">{direction === 'asc' ? '↑' : '↓'}</span>
        </button>
      </ListToolbar>

      <ListWindowNotice view={view} outsideWindow={data?.outsideWindow ?? 0} empty={rows.length === 0 && !query.isLoading} />

      {query.isLoading && !data && <p className="screen__loading">{t('common.loading')}</p>}
      {query.isError && <div className="notice notice--error">{t('audit.unavailable')}</div>}

      {data && rows.length === 0 && (
        <div className="empty">
          <span className="empty__title">{t('audit.emptyTitle')}</span>
          <p className="empty__body">{t('audit.emptyBody')}</p>
        </div>
      )}

      {data && rows.length > 0 && (
        <ol className="journal__list">
          {rows.map((entry) => {
            const fields = Object.entries(entry.changes ?? {});
            const kind = OPENABLE[entry.entity];
            return (
              <li key={entry.id} className="journal__entry">
                <span
                  className="journal__dot"
                  style={{ background: entry.actor?.color ?? 'var(--zinc-400)' }}
                  aria-hidden="true"
                />

                <div className="journal__body">
                  <p className="journal__line">
                    <strong>{entry.actor?.name ?? t('audit.origin.importer')}</strong>{' '}
                    {label('action', entry.action)}{' '}
                    <span className="journal__entity">{label('entity', entry.entity)}</span>
                    {entry.entityId && kind && (
                      <>
                        {' '}
                        <RecordLink kind={kind} id={entry.entityId} className="journal__link">
                          {t('audit.open')}
                        </RecordLink>
                      </>
                    )}
                  </p>

                  {/* Structured facts a diff cannot hold — rendered as pairs, never as prose. */}
                  {Object.keys(entry.detail ?? {}).length > 0 && (
                    <p className="journal__summary">
                      {Object.entries(entry.detail)
                        .map(([k, v]) => `${label('field', k)}: ${format(v, t)}`)
                        .join(' · ')}
                    </p>
                  )}

                  {/* The changed fields. This is what makes a log worth reading. */}
                  {fields.length > 0 && (
                    <ul className="journal__changes">
                      {fields.map(([field, { from, to }]) => (
                        <li key={field}>
                          <span className="journal__field">{label('field', field)}</span>
                          <span className="journal__from">{format(from, t)}</span>
                          <span aria-hidden="true">→</span>
                          <span className="journal__to">{format(to, t)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="journal__meta">
                  <span className="tnum">{when(entry.at, locale)}</span>
                  {/* Where it came from: an edit from a van reads differently from one at a desk. */}
                  <span className={`journal__origin journal__origin--${entry.origin}`}>{label('origin', entry.origin)}</span>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {data && (
        <Pager
          page={data.page}
          pageCount={data.pageCount}
          pageSize={data.pageSize}
          total={data.total}
          count={rows.length}
          label={t('audit.actions')}
          busy={query.isFetching}
          onPage={view.setPage}
          onPageSize={view.setPageSize}
        />
      )}
    </div>
  );
}

/** A changed value, short enough to sit on one line. */
function format(value: unknown, t: (key: string) => string): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length ? `${value.length}` : '—';
  if (typeof value === 'boolean') return t(value ? 'list.yes' : 'list.no');
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
}

function when(iso: string, locale: Locale): string {
  const intl = locale === 'fr' ? 'fr-CA' : 'en-CA';
  const at = new Date(iso);
  const today = new Date();
  const sameDay = at.toDateString() === today.toDateString();
  return sameDay
    ? at.toLocaleTimeString(intl, { hour: '2-digit', minute: '2-digit' })
    : at.toLocaleString(intl, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
